/**
 * Dashboard di tracciamento per singola newsletter (RF-06 / §8.10 / §11.8).
 *
 * Sezioni:
 *  - KPI aggregati (da GET /newsletters/{id}.metrics): inviate, aperture, click, bounce, spam.
 *  - "Funnel" a barre proporzionali (inviate → aperte → cliccate).
 *  - Statistiche per link (GET /newsletters/{id}/link-stats).
 *  - Dettaglio per-destinatario (GET /newsletters/{id}/recipients), paginato con filtri (q, stato, aperture).
 *
 * Nota forma dati: per gli aggregati email si usano stat tile/numeri (non una torta:
 * aperture e click non sono segmenti mutuamente esclusivi).
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  Typography,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableContainer,
  TableFooter,
  TableRow,
  TableCell,
  TablePagination,
  TextField,
  MenuItem,
  Link as MuiLink,
  CircularProgress,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
//--------------------------------------------------------
import { HttpErrorHandler, ErrorUI } from '../../../utils/ErrorHandler';
import { LoadingUI, EmptyPageUI } from '../../../utils/LayoutStyles';
import { theme } from '../../../utils/TableStyles';
import { DefaultOutlinedButton } from '../../../utils/Buttons';
import { usePopup } from '../../../context/PopupContext';
import { trackingApi } from '../../../services/newsletter/trackingApi';
import { StatusBadge } from './components/StatusBadge';

/** Normalizza un tasso in percentuale (accetta 0–1 o 0–100). */
const toPct = (v) => {
  if (v == null || Number.isNaN(Number(v))) return null;
  const n = Number(v);
  return n <= 1 ? n * 100 : n;
};
const fmtPct = (v) => (v == null ? '—' : `${v.toFixed(1)}%`);
const pctOf = (part, base) => (base ? Math.round((Number(part || 0) / base) * 100) : 0);

/** Stati per-destinatario (enum BE) con etichetta e colori. */
const RECIPIENT_STATUS_META = {
  // In attesa: destinatario incluso ma email non ancora partita (batch in coda o interrotto).
  PENDING: { label: 'In attesa', color: '#637381', bg: '#F4F6F8' },
  SENT: { label: 'Inviata', color: '#1B806A', bg: '#D8FBDE' },
  SOFT_BOUNCE: { label: 'Soft bounce', color: '#B76E00', bg: '#FFF1D6' },
  HARD_BOUNCE: { label: 'Hard bounce', color: '#B72136', bg: '#FFE7D9' },
  SPAM: { label: 'Spam', color: '#B76E00', bg: '#FFF1D6' },
  FAILED: { label: 'Fallita', color: '#B72136', bg: '#FFE7D9' },
};

/** Opzioni per il filtro stato. */
const RECIPIENT_STATUS_OPTIONS = Object.entries(RECIPIENT_STATUS_META).map(([value, meta]) => ({
  value,
  label: meta.label,
}));

/** Chip di stato per-destinatario. */
function recipientStatusChip(status) {
  const meta = RECIPIENT_STATUS_META[status] ?? { label: status || '—', color: '#637381', bg: '#F4F6F8' };
  return (
    <Chip size="small" label={meta.label} sx={{ fontWeight: 700, color: meta.color, backgroundColor: meta.bg }} />
  );
}

/** Stat tile per un KPI. */
function StatTile({ label, value, sub }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="caption" sx={{ color: '#637381', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, my: 0.5 }}>
          {value}
        </Typography>
        {sub && (
          <Typography variant="body2" sx={{ color: '#919EAB' }}>
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

StatTile.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sub: PropTypes.node,
};

/** Barra proporzionale del funnel. */
function FunnelBar({ label, count, base, color }) {
  const pct = pctOf(count, base);
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: '#637381' }}>
          {count ?? 0} ({pct}%)
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{ height: 8, borderRadius: 4, backgroundColor: '#F4F6F8', '& .MuiLinearProgress-bar': { backgroundColor: color } }}
      />
    </Box>
  );
}

FunnelBar.propTypes = {
  label: PropTypes.string,
  count: PropTypes.number,
  base: PropTypes.number,
  color: PropTypes.string,
};

/** Grafico circolare del funnel (usato su desktop). */
function FunnelDonut({ label, count, base, color }) {
  const pct = pctOf(count, base);
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        {/* traccia di sfondo */}
        <CircularProgress variant="determinate" value={100} size={120} thickness={4} sx={{ color: '#F0F0F0' }} />
        {/* avanzamento */}
        <CircularProgress
          variant="determinate"
          value={pct}
          size={120}
          thickness={4}
          sx={{ color, position: 'absolute', left: 0 }}
        />
        <Box
          sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {pct}%
          </Typography>
        </Box>
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ color: '#637381' }}>
        {count ?? 0}
      </Typography>
    </Box>
  );
}
FunnelDonut.propTypes = {
  label: PropTypes.string,
  count: PropTypes.number,
  base: PropTypes.number,
  color: PropTypes.string,
};

const PER_PAGE_FALLBACK = 15;

/** Focus in nero (#212b36) invece del primary, coerente con StyledInputs. */
const FILTER_SX = {
  '& label.Mui-focused': { color: '#212b36' },
  '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#212b36' },
};

export default function NewsletterTracking() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const newsID = state?.newsID;
  const { setPopUpText } = usePopup();

  const [currentProfile, setCurrentProfile] = useState('');
  const [newsletter, setNewsletter] = useState(null);
  const [linkStats, setLinkStats] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // recipients (server-side)
  const [recipients, setRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // 0-based
  const [perPage, setPerPage] = useState(PER_PAGE_FALLBACK);

  // filtri
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [openedFilter, setOpenedFilter] = useState(''); // '' | 'true' | 'false'

  useEffect(() => {
    let data;
    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));
    if (data) setCurrentProfile(data.roles.filter((role) => role.last_used));
  }, []);

  // Caricamento aggregati + per-link
  useEffect(() => {
    if (!newsID) return;
    Promise.all([trackingApi.getMetrics(newsID), trackingApi.getLinkStats(newsID)])
      .then(([mRes, lRes]) => {
        setNewsletter(mRes.data);
        setLinkStats(Array.isArray(lRes.data) ? lRes.data : lRes.data?.data ?? []);
      })
      .catch((err) => HttpErrorHandler(err, setErrorMessage))
      .finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newsID]);

  // Caricamento per-destinatario (paginato + filtri, con debounce sui filtri)
  useEffect(() => {
    if (!newsID) return undefined;
    const timer = setTimeout(() => {
      const params = { page: page + 1 };
      if (q.trim()) params.q = q.trim();
      if (statusFilter.trim()) params.status = statusFilter.trim();
      if (openedFilter) params.opened = openedFilter;

      setRecipientsLoading(true);
      trackingApi
        .getRecipients(newsID, params)
        .then((res) => {
          const d = res.data ?? {};
          setRecipients(Array.isArray(d) ? d : d.data ?? d.recipients ?? []);
          setTotal(d.total ?? (Array.isArray(d) ? d.length : 0));
          if (d.per_page) setPerPage(d.per_page);
        })
        .catch((err) => {
          HttpErrorHandler(err, setPopUpText);
        })
        .finally(() => setRecipientsLoading(false));
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newsID, page, q, statusFilter, openedFilter]);

  // reset pagina al cambio filtri
  const onFilterChange = (setter) => (value) => {
    setPage(0);
    setter(value);
  };

  if (currentProfile && currentProfile[0].prof_type_name !== 'ADMIN') return <Navigate to={{ pathname: '/' }} />;
  if (!newsID) return <Navigate to={{ pathname: '/404' }} />;

  const metrics = newsletter?.metrics ?? {};
  const included = Number(metrics.included ?? 0);
  const sent = Number(metrics.sent ?? 0);
  const opened = Number(metrics.opened ?? 0);
  const clicked = Number(metrics.clicked ?? 0);
  const bounce = Number(metrics.soft_bounce ?? 0) + Number(metrics.hard_bounce ?? 0);
  const spam = Number(metrics.spam ?? 0);
  const openRate = toPct(metrics.open_rate);
  const clickRate = sent ? (clicked / sent) * 100 : null;
  const funnelBase = included || sent || 0;

  return (
    <>
      <Helmet>
        <title>Tracciamento newsletter</title>
      </Helmet>

      <Container maxWidth="xl">
        {loaded ? (
          errorMessage.length === 0 ? (
            <Stack direction="column" gap="24px">
              {/* Header */}
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap="12px">
                <Stack direction="row" alignItems="center" spacing={2}>
                  <DefaultOutlinedButton
                    startIcon={<ArrowBackIcon />}
                    onClick={() =>
                      navigate('/admin/newsletter/newsletter-details', { state: { newsID } })
                    }
                  >
                    Indietro
                  </DefaultOutlinedButton>
                  <Typography variant="h5">{newsletter?.name || 'Tracciamento'}</Typography>
                  <StatusBadge status={newsletter?.status} />
                </Stack>
              </Stack>

              {/* KPI */}
              <Grid container spacing={2}>
                <Grid item xs={6} md={2.4}>
                  <StatTile label="Inviate" value={sent} sub={`su ${included} destinatari`} />
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <StatTile label="Aperture" value={opened} sub={`${fmtPct(openRate)} tasso apertura`} />
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <StatTile label="Click" value={clicked} sub={`${fmtPct(clickRate)} tasso click`} />
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <StatTile label="Bounce" value={bounce} sub="mancate consegne" />
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <StatTile label="Spam" value={spam} sub="segnalazioni" />
                </Grid>
              </Grid>

              {/* Funnel */}
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                    Andamento
                  </Typography>

                  {/* Desktop (lg+): 3 grafici circolari affiancati */}
                  <Stack
                    direction="row"
                    justifyContent="space-around"
                    alignItems="center"
                    spacing={3}
                    sx={{ display: { xs: 'none', lg: 'flex' }, py: 1 }}
                  >
                    <FunnelDonut label="Inviate" count={sent} base={funnelBase} color="#1939B7" />
                    <FunnelDonut label="Aperte" count={opened} base={funnelBase} color="#1B806A" />
                    <FunnelDonut label="Cliccate" count={clicked} base={funnelBase} color="#B76E00" />
                  </Stack>

                  {/* Mobile/tablet: barre lineari */}
                  <Stack spacing={2} sx={{ display: { xs: 'flex', lg: 'none' } }}>
                    <FunnelBar label="Inviate" count={sent} base={funnelBase} color="#1939B7" />
                    <FunnelBar label="Aperte" count={opened} base={funnelBase} color="#1B806A" />
                    <FunnelBar label="Cliccate" count={clicked} base={funnelBase} color="#B76E00" />
                  </Stack>
                </CardContent>
              </Card>

              {/* Per-link */}
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Click per link
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Link</TableCell>
                          <TableCell align="right">Click totali</TableCell>
                          <TableCell align="right">Click unici</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {linkStats.length > 0 ? (
                          linkStats.map((l) => (
                            <TableRow hover key={l.id}>
                              <TableCell sx={{ maxWidth: 480 }}>
                                <MuiLink href={l.original_url} target="_blank" rel="noopener" sx={{ wordBreak: 'break-all' }}>
                                  {l.original_url}
                                </MuiLink>
                              </TableCell>
                              <TableCell align="right">{l.total_clicks ?? 0}</TableCell>
                              <TableCell align="right">{l.unique_clicks ?? 0}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3}>{EmptyPageUI('Nessun click registrato')}</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Per-destinatario */}
              <Card>
                <CardContent>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    spacing={2}
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Dettaglio destinatari
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <TextField
                        size="small"
                        label="Cerca email"
                        value={q}
                        onChange={(e) => onFilterChange(setQ)(e.target.value)}
                        sx={FILTER_SX}
                      />
                      <TextField
                        select
                        size="small"
                        label="Stato"
                        value={statusFilter}
                        onChange={(e) => onFilterChange(setStatusFilter)(e.target.value)}
                        sx={{ ...FILTER_SX, minWidth: 160 }}
                      >
                        <MenuItem value="">Tutti</MenuItem>
                        {RECIPIENT_STATUS_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        select
                        size="small"
                        label="Aperture"
                        value={openedFilter}
                        onChange={(e) => onFilterChange(setOpenedFilter)(e.target.value)}
                        sx={{ ...FILTER_SX, minWidth: 140 }}
                      >
                        <MenuItem value="">Tutti</MenuItem>
                        <MenuItem value="true">Aperte</MenuItem>
                        <MenuItem value="false">Non aperte</MenuItem>
                      </TextField>
                    </Stack>
                  </Stack>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Email</TableCell>
                          <TableCell>Utente</TableCell>
                          <TableCell align="center">Stato</TableCell>
                          <TableCell align="center">Aperture</TableCell>
                          <TableCell align="center">Click</TableCell>
                          <TableCell>Note</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recipientsLoading ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                              <CircularProgress size={24} />
                            </TableCell>
                          </TableRow>
                        ) : recipients.length > 0 ? (
                          recipients.map((r) => (
                            <TableRow hover key={r.id ?? r.email}>
                              <TableCell sx={{ wordBreak: 'break-all' }}>{r.email}</TableCell>
                              <TableCell>
                                {r.user ? `${r.user.name ?? ''} ${r.user.surname ?? ''}`.trim() || '—' : '—'}
                              </TableCell>
                              <TableCell align="center">{recipientStatusChip(r.status)}</TableCell>
                              <TableCell align="center">
                                {r.open_count ?? 0}
                                {r.opened_at && (
                                  <Typography variant="caption" sx={{ display: 'block', color: '#919EAB' }}>
                                    {dayjs(r.opened_at).format('DD/MM/YY HH:mm')}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">{r.clicks_count ?? 0}</TableCell>
                              <TableCell sx={{ color: '#B72136' }}>{r.error_reason || ''}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={6}>{EmptyPageUI('Nessun destinatario trovato')}</TableCell>
                          </TableRow>
                        )}
                      </TableBody>

                      {total > perPage && (
                        <TableFooter>
                          <TableRow>
                            <ThemeProvider theme={theme}>
                              <TablePagination
                                sx={{ border: 0 }}
                                rowsPerPageOptions={[]}
                                colSpan={6}
                                count={total}
                                rowsPerPage={perPage}
                                page={page}
                                onPageChange={(e, newPage) => setPage(newPage)}
                                labelRowsPerPage="Righe per Pagina"
                              />
                            </ThemeProvider>
                          </TableRow>
                        </TableFooter>
                      )}
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Stack>
          ) : (
            ErrorUI(errorMessage)
          )
        ) : (
          LoadingUI()
        )}
      </Container>
    </>
  );
}
